import { AxiosResponse } from "axios";
import { flatten, orderBy } from "lodash";
import axios, { APIWrapperResponse, DefaultResponse } from "../general/HttpClient";
import { LeagueViewModel } from "../leagues/LeaguesStore";
import { MatchViewModel } from "../matches/MatchesStore";
import { TeamViewModel } from "../teams/TeamsStore";

export function getSeasons(leagueID) {
    return axios.get("/api/seasons/getLeagueSeasons", { params: { leagueID } }).then((response: AxiosResponse<APIWrapperResponse<Array<SeasonViewModel>>>) => {
        return response.data.result;
    });
}

// export function getTeamSeasons(id) {
//     return axios.get(`/api/seasons/getTeamSeasons/${id}`).then((response: AxiosResponse<APIWrapperResponse<Array<SeasonViewModel>>>) => {
//         return response.data.result;
//     });
// }

// export function getAliasSeasons(id) {
//     return axios.get(`/api/seasons/getAliasSeasons/${id}`).then((response: AxiosResponse<APIWrapperResponse<Array<SeasonViewModel>>>) => {
//         return response.data.result;
//     });
// }

export function getSeason(id) {
    return axios.get(`/api/seasons/getSeason/${id}`).then((response: AxiosResponse<APIWrapperResponse<SeasonViewModel>>) => {
        return response.data.result;
    });
}

export function createSeason(request: CreateSeasonRequest) {
    return axios.post(`/api/seasons/createSeason`, request).then((response: AxiosResponse<APIWrapperResponse<CreateSeasonResponse>>) => {
        return response.data.result;
    });
}

export function updateSeason(request: SeasonViewModel | UpdateSeasonRequest) {
    return axios.put(`/api/seasons/updateSeason`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export function addParticipant(request: AddSeasonParticipantRequest) {
    return axios.post(`/api/Seasons/addParticipant`, request).then((response: AxiosResponse<APIWrapperResponse<AddSeasonParticipantResponse>>) => {
        return response.data.result;
    });
}

export function removeParticipant(request: RemoveSeasonParticipantRequest) {
    return axios.post(`/api/Seasons/removeParticipant`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export function implementSchedule(request: ImplementSeasonScheduleRequest) {
    return axios.post(`/api/Seasons/implementSchedule`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export function updateSchedule(request: UpdateSeasonScheduleRequest) {
    return axios.post(`/api/Seasons/updateSchedule`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export function revertSchedule(request: RevertSeasonScheduleRequest) {
    return axios.post(`/api/Seasons/revertSchedule`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export function deleteSchedule(request: DeleteSeasonScheduleRequest) {
    return axios.post(`/api/Seasons/deleteSchedule`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export function createMatchSchedule(season: SeasonViewModel, request: SeasonScheduleSettings) {
    return axios.post(`/api/Seasons/${season.id}/createMatchSchedule`, request).then((response: AxiosResponse<APIWrapperResponse<CreateMatchScheduleResponse>>) => {
        return response.data.result;
    });
}

export function implementMatchSchedule(request: ImplementMatchScheduleRequest) {
    return axios.post(`/api/Seasons/implementMatchSchedule`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export class TeamStatisticsViewModel {
    teamID: string;
    team: TeamViewModel;
    statistics: [{ key: string; value: number }];
}

export class SeasonViewModel {
    constructor() {
        this.participants = [];
        this.league = new LeagueViewModel();
        this.schedule = new SeasonScheduleViewModel();
    }
    id?: string;
    name: string;
    rules: string;
    rulesAsMarkdown: boolean;
    logoUri: string;
    splashUri: string;
    color: string;
    archived: boolean;
    startDate: string;
    endDate: string;
    locked?: boolean;
    isPublic?: boolean;
    leagueID: string;
    league?: LeagueViewModel;

    schedule: SeasonScheduleViewModel;
    scheduleType: string;

    participants: SeasonParticipantViewModel[];
    participantCount: number;

    teamStatistics: TeamStatisticsViewModel[];

    matches: MatchViewModel[];
}

export class SeasonScheduleViewModel {
    constructor() {
        this.matches = [];
        this.requiredMatches = 0;
        this.rounds = [];
    }
    matches: SeasonMatchViewModel[];
    requiredMatches: number;
    rounds: SeasonRound[];
}

export class SeasonMatchViewModel {
    seasonMatchID: number;
    sortKey: number;
    matchID: string;
    participantTeamIDs: string[];
    matchStartDate: string;
    match?: MatchViewModel;
}

export class SeasonRound {
    roundNumber: number;
    matches: SeasonMatchViewModel[];
}

export class CreateSeasonRequest {
    leagueID: string;
    name: string;
    rules: string;
    rulesAsMarkdown: boolean;
    logoUri: string;
    logoContent: string;
    splashUri: string;
    splashContent: string;
    color: string;
    startDate: string;
    endDate: string;
    isPublic: boolean;
}

export interface CreateSeasonResponse extends DefaultResponse {
    seasonID: string;
}

export interface UpdateSeasonRequest {
    id: string;
    name: string;
    rules: string;
    rulesAsMarkdown: boolean;
    logoUri: string;
    logoContent: string;
    splashUri: string;
    splashContent: string;
    color: string;
    isPublic: boolean;
    scheduleType: string;
}

export interface SeasonParticipantViewModel {
    id: string;
    teamID: string;
    team: TeamViewModel;
}

export interface RemoveSeasonParticipantRequest {
    seasonID: string;
    teamID: string;
}

export interface AddSeasonParticipantResponse extends DefaultResponse {
    participantID: string;
}

export interface AddSeasonParticipantRequest {
    seasonID: string;
    teamID: string;
}

export interface ImplementSeasonScheduleRequest {
    seasonID: string;
}

export interface UpdateSeasonScheduleRequest {
    id: string;
    schedule: SeasonScheduleViewModel;
}

export interface SeasonScheduleSettings {
    scheduleTimeOnly: boolean;
    concurrentMatches: number;
    matchesPerDay: number;
    matchDurationMinutes: number;
    matchesGapMinutes: number;
    availableDays: number[];
    startTime: string;
}

export interface CreateMatchScheduleResponse extends DefaultResponse {
    rounds: SeasonRound[];
}

export interface ImplementMatchScheduleRequest {
    seasonID: string;
    settings: SeasonScheduleSettings;
}

export interface DeleteSeasonScheduleRequest {
    seasonID: string;
}

export interface RevertSeasonScheduleRequest {
    seasonID: string;
}
